import is from '@sindresorhus/is';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../context/context';
import { endorsedLanguageSkillArraySchema } from '../data-contract/skill';
import { userLanguageSkillsOptionsSchema } from '../data-contract/user-language-skills';

import type { EndorsedLanguageSkill, LanguageSkillMutation, SkillMutationSource } from '../data-contract/skill';
import type { UserLanguageSkillsOptions } from '../data-contract/user-language-skills';
import type { Pathname } from '../http-fetcher/http-fetcher';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

const getUserLanguageSkillQueryKey = (userId: string): readonly [`/${string}`, string] => {
    const pathname = `/users/${userId}/languages` as const;

    return [pathname, 'get'];
};

export const useUserLanguageSkillsOptionsQuery = (userId: string): UseQueryResult<UserLanguageSkillsOptions> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/languages/options`;

    return useQuery({
        enabled: is.nonEmptyString(userId),
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userLanguageSkillsOptionsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', userLanguageSkillsOptionsSchema],
    });
};

export const useUserLanguageSkillsQuery = ({
    enabled,
    userId,
}: {
    readonly enabled: boolean;
    readonly userId: string;
}): UseQueryResult<ReadonlyArray<EndorsedLanguageSkill>> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const [pathname, httpOperation] = getUserLanguageSkillQueryKey(userId);

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(endorsedLanguageSkillArraySchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, httpOperation],
    });
};

export type UserLanguageSkillsMutationResult = UseMutationResult<void, Error, ReadonlyArray<LanguageSkillMutation>>;

export const useUserLanguageSkillsMutation = (
    userId: string,
    source: SkillMutationSource,
): UserLanguageSkillsMutationResult => {
    const { cobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/users/${userId}/languages`;

    const queryClient = useQueryClient();
    const invalidateQueries = (): void => {
        queryClient.invalidateQueries({ queryKey: getUserLanguageSkillQueryKey(userId) });
    };

    return useMutation({
        mutationFn: async (payload) => {
            const result = await cobrainerApiFetcher.postJson({
                pathname,
                payload,
                requestOptions: {
                    searchParams: { source },
                },
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onSuccess: invalidateQueries,
    });
};
